import type {
  AgentWidgetConfig,
  Message,
  WidgetConfig,
  WidgetMode,
} from "../types";
import type { BridgeMessage, BridgeRequest, BridgeResponse } from "./types";

/**
 * Command types that can be sent to the widget
 */
export type Command =
  | { type: "OPEN" }
  | { type: "CLOSE" }
  | { type: "SEND_MESSAGE"; payload: { message: string } }
  | { type: "UPDATE_CONFIG"; payload: Partial<WidgetConfig> }
  | { type: "SET_API_KEY"; payload: string }
  | {
      type: "SET_INITIAL_CONFIG";
      payload: {
        apiKey: string;
        agentCustomData?: Record<string, unknown>;
        stream?: boolean;
        mode?: WidgetMode;
        overrides?: Partial<AgentWidgetConfig>;
      };
    }
  | { type: "SET_CUSTOM_DATA"; payload: Record<string, unknown> }
  | { type: "START_CONVERSATION" }
  | { type: "GET_MESSAGES" }
  | { type: "CLEAR_CONVERSATION" }
  | { type: "PING" };

/**
 * Event types that can be received from the widget
 */
export type Event =
  | { type: "READY" }
  | { type: "OPENED" }
  | { type: "CLOSED" }
  | { type: "MESSAGE_RECEIVED"; payload: { message: Message } }
  | { type: "CONVERSATION_STARTED"; payload: { conversationId: string } }
  | { type: "AI_STREAMING"; payload: { token: string; isMcp?: boolean } }
  | { type: "AI_DONE"; payload: { message: Message } }
  | { type: "ERROR"; payload: { error: string; code?: string } }
  | { type: "CONFIG_LOADED"; payload: { primaryColor?: string; textColor?: string } }
  | { type: "PONG" };

export function createCommandMessage(command: Command): BridgeRequest {
  return {
    id: generateMessageId(),
    type: command.type,
    payload: "payload" in command ? command.payload : undefined,
    timestamp: Date.now(),
    expectsResponse:
      command.type === "PING" ||
      command.type === "START_CONVERSATION" ||
      command.type === "GET_MESSAGES" ||
      command.type === "CLEAR_CONVERSATION",
  };
}

export function createEventMessage(event: Event): BridgeMessage {
  return {
    id: generateMessageId(),
    type: event.type,
    payload: "payload" in event ? event.payload : undefined,
    timestamp: Date.now(),
  };
}

export function createResponseMessage(
  requestId: string,
  success: boolean,
  data?: unknown,
  error?: string
): BridgeResponse {
  return {
    id: generateMessageId(),
    type: "RESPONSE",
    payload: data,
    timestamp: Date.now(),
    requestId,
    success,
    error,
  };
}

export function isCommand(
  message: BridgeMessage
): message is BridgeRequest & { type: Command["type"] } {
  return [
    "OPEN",
    "CLOSE",
    "SEND_MESSAGE",
    "UPDATE_CONFIG",
    "SET_API_KEY",
    "SET_INITIAL_CONFIG",
    "SET_CUSTOM_DATA",
    "START_CONVERSATION",
    "GET_MESSAGES",
    "CLEAR_CONVERSATION",
    "PING",
  ].includes(message.type);
}

export function isEvent(
  message: BridgeMessage
): message is BridgeMessage & { type: Event["type"] } {
  return [
    "READY",
    "OPENED",
    "CLOSED",
    "MESSAGE_RECEIVED",
    "CONVERSATION_STARTED",
    "AI_STREAMING",
    "AI_DONE",
    "ERROR",
    "CONFIG_LOADED",
    "PONG",
  ].includes(message.type);
}

export function isResponse(message: BridgeMessage): message is BridgeResponse {
  return message.type === "RESPONSE";
}

function generateMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
